import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";

const AnalysisContext = createContext(null);

const STORAGE_KEY = "analysis_queue_v3";
const RAILWAY_URL = "https://web-production-c0e77.up.railway.app";

// ── LocalStorage yardımcıları ─────────────────────────────────────────────
function saveQueue(q) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(q)); } catch {}
}

function loadQueue() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    // Sayfa kapanıp açılınca: job_id'si olanlar "polling" statüsüne geçsin
    return parsed.map(q => {
      if (q.job_id && (q.status === "loading" || q.status === "polling")) {
        return { ...q, status: "polling" }; // job Railway'de devam ediyor olabilir
      }
      if (q.status === "waiting") {
        return { ...q, status: "waiting" }; // henüz başlamamış, yeniden başlatılacak
      }
      return q;
    });
  } catch { return []; }
}

// ── Job durumu sorgula ────────────────────────────────────────────────────
async function pollJobStatus(jobId) {
  const r = await fetch(`${RAILWAY_URL}/api/predict/job/${jobId}`);
  if (!r.ok) throw new Error("job bulunamadı");
  return await r.json();
}

export function AnalysisProvider({ children }) {
  const [queue, setQueue] = useState(loadQueue);
  const runningRef = useRef(false);
  const queueRef   = useRef(queue);
  const pollTimers = useRef({}); // job_id → interval
  // Auth'u ref'te tut — startJob/addToQueue useCallback([]) olduğu için taze
  // token/callback'lere ref üzerinden erişilir (stale closure önlenir).
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  const updateQueue = useCallback((updater) => {
    setQueue(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queueRef.current = next;
      saveQueue(next);
      return next;
    });
  }, []);

  // ── Tek bir job'ı Railway'den başlat ve poll et ───────────────────────
  const startJob = useCallback(async (item) => {
    updateQueue(prev => prev.map(q =>
      q.id === item.id ? { ...q, status: "loading" } : q
    ));

    try {
      const token = authRef.current?.getToken?.();
      // Railway'de job başlat — analiz artık üyelik ister, token eklenir.
      const res = await fetch(`${RAILWAY_URL}/api/predict`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          home: item.home, away: item.away,
          league: item.league, ai_provider: item.aiProvider || "gpt",
          // Maçın GERÇEK günü (son-analiz-geçerli için; item.date maç günü).
          match_date: item.date || new Date().toISOString().slice(0, 10),
        }),
      });

      // Yetki / kota yanıtları — kuyruğu döngüye sokmadan dur.
      if (res.status === 401) {
        // Token yok veya süresi dolmuş → giriş modalını KESİN aç (requireLogin
        // token varsa açmıyor; süresi dolmuşsa da açmalı).
        authRef.current?.setLoginOpen?.(true);
        updateQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: "error" } : q));
        processNext(); return;
      }
      if (res.status === 402 || res.status === 403 || res.status === 429) {
        const body = await res.json().catch(() => ({}));
        authRef.current?.showPricing?.(body.detail || "Analiz limitine ulaşıldı.");
        updateQueue(prev => prev.map(q => q.id === item.id ? { ...q, status: "error" } : q));
        processNext(); return;
      }

      const { job_id } = await res.json();
      updateQueue(prev => prev.map(q =>
        q.id === item.id ? { ...q, job_id, status: "polling" } : q
      ));
      startPolling(item.id, job_id);

    } catch {
      updateQueue(prev => prev.map(q =>
        q.id === item.id ? { ...q, status: "error" } : q
      ));
      processNext();
    }
  }, []); // eslint-disable-line

  // ── Job'ı poll et ────────────────────────────────────────────────────
  const startPolling = useCallback((itemId, jobId) => {
    // Önceki timer varsa temizle
    if (pollTimers.current[jobId]) clearInterval(pollTimers.current[jobId]);

    const timer = setInterval(async () => {
      try {
        const job = await pollJobStatus(jobId);

        if (job.status === "done") {
          clearInterval(pollTimers.current[jobId]);
          delete pollTimers.current[jobId];

          const data = job.result;

          // NOT: Supabase kaydını ARTIK backend yapıyor (_run_predict_job,
          // doğru match_date + updated_at ile). Eskiden burada ikinci bir
          // upsert vardı; backend bugüne, buradaki maç gününe yazdığı için
          // AYNI maç için İKİ satır oluşuyordu. Tek yazma yolu = tek satır.
          updateQueue(prev => prev.map(q =>
            q.id === itemId ? { ...q, status: "done", result: data } : q
          ));
          authRef.current?.refreshPlan?.();   // kalan hak rozetini tazele
          processNext();

        } else if (job.status === "error") {
          clearInterval(pollTimers.current[jobId]);
          delete pollTimers.current[jobId];
          updateQueue(prev => prev.map(q =>
            q.id === itemId ? { ...q, status: "error" } : q
          ));
          processNext();
        }
        // "running" ise poll etmeye devam et
      } catch {
        // Job bulunamadı — Railway restart olmuş olabilir
        clearInterval(pollTimers.current[jobId]);
        delete pollTimers.current[jobId];
        // Waiting'e geri al — yeniden başlatılacak
        updateQueue(prev => prev.map(q =>
          q.id === itemId ? { ...q, status: "waiting", job_id: null } : q
        ));
        if (!runningRef.current) {
          runningRef.current = true;
          setTimeout(processNext, 500);
        }
      }
    }, 3000); // 3 saniyede bir sorgula

    pollTimers.current[jobId] = timer;
  }, []); // eslint-disable-line

  // ── Sıradaki bekleyen item'ı işle ────────────────────────────────────
  const processNext = useCallback(() => {
    const waiting = queueRef.current.filter(q => q.status === "waiting");
    const loading  = queueRef.current.filter(q => q.status === "loading" || q.status === "polling");

    // Aynı anda max 3 paralel analiz
    const slots = 3 - loading.length;
    if (slots <= 0 || waiting.length === 0) {
      if (loading.length === 0) runningRef.current = false;
      return;
    }

    const toStart = waiting.slice(0, slots);
    toStart.forEach(item => startJob(item));
  }, [startJob]);

  // ── Sayfa açılınca: eski kayıtları temizle, aktifleri sürdür ────────
  useEffect(() => {
    // Bitmiş/hatalı (done/error) eski kayıtları at — birikip yeni analizi
    // engellememeliler. Yalnızca aktif (waiting/loading/polling) kalsın.
    const temiz = queueRef.current.filter(
      item => item.status === "waiting" || item.status === "loading" || item.status === "polling"
    );
    if (temiz.length !== queueRef.current.length) updateQueue(temiz);

    // Polling item'larını yeniden bağla (sayfa kapanmış olabilir).
    temiz.filter(item => item.status === "polling" && item.job_id).forEach(item => {
      startPolling(item.id, item.job_id);
    });
    // Waiting item'ları başlat.
    if (temiz.some(item => item.status === "waiting")) {
      runningRef.current = true;
      setTimeout(processNext, 500);
    }
  }, []); // eslint-disable-line

  // ── Cleanup ───────────────────────────────────────────────────────────
  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      Object.values(timers).forEach(clearInterval);
    };
  }, []);

  // ── addToQueue ────────────────────────────────────────────────────────
  const addToQueue = useCallback((matches, league, existingPredictions, aiProvider = "gpt") => {
    // Analiz üyelik ister — giriş yoksa kuyruğa alma, giriş modalını aç.
    if (!authRef.current?.requireLogin?.()) return;
    const newItems = [];
    const removeIds = [];   // temizlenecek eski (done/error/waiting) kayıtlar

    for (const m of matches) {
      // Kullanıcının AÇTIĞI (unlock) tahmin varsa tekrar analiz etme.
      const existing = existingPredictions?.find(
        p => p.home === m.home && p.away === m.away && p.match_date === m.date
      );
      if (existing) continue;

      const q = queueRef.current.find(
        x => x.home === m.home && x.away === m.away && x.date === m.date
      );
      // AKTİF çalışıyorsa (loading/polling) dokunma. Aksi halde (done/error/
      // waiting → önceki denemeden kalma) eski kaydı SİL ve taze ekle.
      // Eskiden herhangi bir statü "zaten kuyrukta" sayılıp yeni analizi
      // SESSİZCE engelliyordu — kullanıcıya "hiçbir şey olmuyor" gibi geliyordu.
      if (q && (q.status === "loading" || q.status === "polling")) continue;
      if (q) removeIds.push(q.id);

      newItems.push({
        id: `${m.home}|${m.away}|${m.date}|${Date.now()}`,
        home: m.home, away: m.away, league,
        date: m.date || new Date().toISOString().slice(0, 10),
        status: "waiting", result: null, aiProvider, job_id: null,
      });
    }

    if (newItems.length === 0 && removeIds.length === 0) return;
    updateQueue(prev => [...prev.filter(x => !removeIds.includes(x.id)), ...newItems]);
    // processNext idempotent (yalnızca waiting'leri başlatır, slot kadar).
    // runningRef guard'ına GÜVENME — takılı kalmış olabilir; her eklemede tetikle.
    if (newItems.length > 0) {
      runningRef.current = true;
      setTimeout(processNext, 100);
    }
  }, [processNext, updateQueue]);

  const clearDone = useCallback(() => {
    updateQueue(prev => prev.filter(q => q.status !== "done" && q.status !== "error"));
  }, [updateQueue]);

  const runningCount = queue.filter(q =>
    q.status === "loading" || q.status === "waiting" || q.status === "polling"
  ).length;

  return (
    <AnalysisContext.Provider value={{ queue, addToQueue, clearDone, runningCount }}>
      {children}
    </AnalysisContext.Provider>
  );
}

export function useAnalysis() {
  return useContext(AnalysisContext);
}
